import type {
  AdminUserItem,
  AdminTeamItem,
  BackupItem,
  DataRetentionPolicyConfig,
  AdminOrgProfile,
  ModulePlanItem,
} from './types';

export function adaptAdminUser(raw: any): AdminUserItem {
  if (!raw || typeof raw !== 'object') {
    return {
      id: 'user-fallback',
      name: 'Unknown User',
      email: 'user@example.com',
      role: 'Member',
      status: 'active',
      created_at: new Date().toISOString(),
    };
  }
  return {
    id: String(raw.id || 'user-fallback'),
    tenant_id: raw.tenant_id ? String(raw.tenant_id) : undefined,
    name: String(raw.name || 'Unknown User'),
    email: String(raw.email || 'user@example.com'),
    role: String(raw.role || 'Member'),
    status: (raw.status as any) || 'active',
    team_id: raw.team_id ? String(raw.team_id) : undefined,
    team_name: raw.team_name ? String(raw.team_name) : undefined,
    last_login_at: raw.last_login_at ? String(raw.last_login_at) : undefined,
    created_at: String(raw.created_at || new Date().toISOString()),
  };
}

export function adaptAdminTeam(raw: any): AdminTeamItem {
  if (!raw || typeof raw !== 'object') {
    return {
      id: 'team-fallback',
      name: 'General',
      description: 'General workspace team',
      lead_name: 'Lead',
      member_count: 1,
      created_at: new Date().toISOString(),
    };
  }
  return {
    id: String(raw.id || 'team-fallback'),
    tenant_id: raw.tenant_id ? String(raw.tenant_id) : undefined,
    name: String(raw.name || 'General Team'),
    description: String(raw.description || ''),
    lead_name: String(raw.lead_name || 'Unassigned'),
    member_count: Number(raw.member_count) || 0,
    created_at: String(raw.created_at || new Date().toISOString()),
  };
}

export function adaptBackup(raw: any): BackupItem {
  if (!raw || typeof raw !== 'object') {
    return {
      id: 'bak-fallback',
      name: 'snapshot.dump',
      size_mb: 12.5,
      status: 'verified',
      checksum_sha256: 'sha256:000',
      created_at: new Date().toISOString(),
    };
  }
  return {
    id: String(raw.id || 'bak-fallback'),
    tenant_id: raw.tenant_id ? String(raw.tenant_id) : undefined,
    name: String(raw.name || 'snapshot.dump'),
    size_mb: Number(raw.size_mb) || 0,
    status: (raw.status as any) || 'verified',
    checksum_sha256: String(raw.checksum_sha256 || ''),
    created_at: String(raw.created_at || new Date().toISOString()),
  };
}

export function adaptDataRetention(raw: any): DataRetentionPolicyConfig {
  if (!raw || typeof raw !== 'object') {
    return {
      audit_log_days: 365,
      timeline_days: 180,
      trash_purge_days: 30,
      legal_hold_active: false,
      held_entities: [],
      auto_anonymize_gdpr: false,
      last_purge_run: new Date().toISOString(),
      excluded_by_hold_count: 0,
      updated_at: new Date().toISOString(),
    };
  }
  return {
    tenant_id: raw.tenant_id ? String(raw.tenant_id) : undefined,
    audit_log_days: Number(raw.audit_log_days) || 365,
    timeline_days: Number(raw.timeline_days) || 180,
    trash_purge_days: Number(raw.trash_purge_days) || 30,
    legal_hold_active: Boolean(raw.legal_hold_active),
    legal_hold_reason: raw.legal_hold_reason ? String(raw.legal_hold_reason) : undefined,
    held_entities: Array.isArray(raw.held_entities) ? raw.held_entities.map(String) : [],
    auto_anonymize_gdpr: Boolean(raw.auto_anonymize_gdpr),
    last_purge_run: String(raw.last_purge_run || new Date().toISOString()),
    excluded_by_hold_count: Number(raw.excluded_by_hold_count) || 0,
    updated_at: String(raw.updated_at || new Date().toISOString()),
  };
}

export function adaptOrgProfile(raw: any): AdminOrgProfile {
  if (!raw || typeof raw !== 'object') {
    return {
      company_name: 'Helios Corporation',
      legal_name: 'Helios Global Holdings Inc.',
      domain: 'helios.example.com',
      timezone: 'UTC',
      currency: 'USD',
      fiscal_year_start: '01-01',
      support_email: 'ops@helios.local',
    };
  }
  return {
    tenant_id: raw.tenant_id ? String(raw.tenant_id) : undefined,
    company_name: String(raw.company_name || 'Helios Corporation'),
    legal_name: String(raw.legal_name || 'Helios Global Holdings Inc.'),
    domain: String(raw.domain || 'helios.example.com'),
    timezone: String(raw.timezone || 'UTC'),
    currency: String(raw.currency || 'USD'),
    fiscal_year_start: String(raw.fiscal_year_start || '01-01'),
    support_email: String(raw.support_email || 'ops@helios.local'),
    logo_url: raw.logo_url ? String(raw.logo_url) : undefined,
    updated_at: raw.updated_at ? String(raw.updated_at) : undefined,
  };
}

export function adaptModulePlan(raw: any): ModulePlanItem {
  if (!raw || typeof raw !== 'object') {
    return {
      module_id: 'core.basic',
      module_name: 'Core Directory',
      tier: 'mandatory',
      is_active: true,
      allocated_seats: 50,
      used_seats: 12,
    };
  }
  return {
    module_id: String(raw.module_id || 'core.basic'),
    module_name: String(raw.module_name || 'Core Module'),
    tier: (raw.tier as any) || 'core',
    is_active: Boolean(raw.is_active),
    allocated_seats: Number(raw.allocated_seats) || 0,
    used_seats: Number(raw.used_seats) || 0,
  };
}

export const sampleAdminUsers: AdminUserItem[] = [
  { id: '1', name: 'Alice Admin', email: 'alice@helios.local', role: 'Super Admin', status: 'active', created_at: new Date().toISOString() },
  { id: '2', name: 'Bob Engineer', email: 'bob@helios.local', role: 'Developer', status: 'active', created_at: new Date().toISOString() },
];

export const sampleAdminTeams: AdminTeamItem[] = [
  { id: '1', name: 'Executive Suite', description: 'C-Level leadership', lead_name: 'Alice Admin', member_count: 3, created_at: new Date().toISOString() },
  { id: '2', name: 'Operations & Logistics', description: 'Supply chain engineers', lead_name: 'Sara Dock', member_count: 8, created_at: new Date().toISOString() },
];

export const sampleBackups: BackupItem[] = [
  { id: '1', name: 'daily-snapshot-20261016.dump', size_mb: 245.8, status: 'verified', checksum_sha256: 'sha256:e3b0c44298fc1c149afbf4c8996fb92427ae41e4649b934ca495991b7852b855', created_at: new Date().toISOString() },
];
